import React from 'react';
import { Link } from 'react-router-dom';
import { ArrowRight } from 'lucide-react';
import { Header } from '../components/Header';
import { Footer } from '../components/Footer';
import { QuoteBlock } from '../components/QuoteBlock';
import { PageHero } from '../components/blocks/PageHero';
import { TextImageSection } from '../components/blocks/TextImageSection';
import { ServicesGrid } from '../components/blocks/ServicesGrid';
import { JournalCarousel } from '../components/blocks/JournalCarousel';
import { TestimonialsGrid } from '../components/blocks/TestimonialsGrid';
import { FaqSection } from '../components/blocks/FaqSection';
import { CtaBanner } from '../components/blocks/CtaBanner';
import { SectionDivider, SURFACE } from '../components/blocks/SectionDivider';
import { servicesData } from '../data/services';
import { blogPosts } from '../data/blog';
import { testimonials } from '../data/testimonials';
import { useSeo } from '../lib/useSeo';
import { faqSchema, serviceSchema } from '../lib/schema';
import { SITE } from '../lib/siteConfig';

/**
 * PAGE: /
 * WORDPRESS: `front-page.php`
 *
 * Blocks: hero → text_image → quote → services_grid → journal_carousel →
 *         testimonials_grid → faq → cta_banner
 */

const HERO_IMAGE = 'https://cdn.magicpatterns.com/uploads/rNSyYD7BhQbyjDzyPNZ74D/milangela_home.jpg';
const INTRODUCTION_IMAGE = 'https://cdn.magicpatterns.com/uploads/od2K7oMTHM4Pz68GiKC2tP/milangela_homepage.webp';

/** ACF: repeater `page_faqs` — the site-level questions AI engines ask first. */
const faqs = [{
  question: "What is Milangela's Soul Garden?",
  answer: `${SITE.name} is a wellbeing practice founded by Branka Androjna, offering Biofield Tuning sound therapy, guided meditation and Sopotje guidance sessions. The intention is not to fix anyone, but to create the conditions in which people can reconnect with themselves.`
}, {
  question: 'What services are offered?',
  answer: 'Three paths: Meditation Between Worlds (an active guided meditation practice, €15 per session or €50 monthly), Sound-Infused Renewal (a one-to-one Biofield Tuning session, €100), and Sopotje (compassionate guidance and presence conversations, €60 per hour).'
}, {
  question: 'Where are sessions held?',
  answer: 'Sessions take place in person in Slovenia and online worldwide. Meditation and Sopotje work equally well remotely; Biofield Tuning is usually offered in the studio.'
}, {
  question: 'Do I need any experience to begin?',
  answer: 'None at all. Every session is fully guided, and you are welcome exactly as you are. If you are unsure which path suits you, a short conversation is usually enough to find out.'
}];
export function Home() {
  useSeo({
    title: `${SITE.name} — Biofield Tuning, Meditation & Presence`,
    description: SITE.description,
    path: '/',
    image: HERO_IMAGE,
    schema: [...servicesData.map(service => serviceSchema({
      name: service.title,
      description: service.cardSummary,
      path: service.link,
      image: service.image,
      price: service.priceNumeric,
      serviceType: service.shortTitle
    })), faqSchema(faqs)]
  });
  const latestPosts = [...blogPosts].sort((a, b) => new Date(b.date).getTime() - new Date(a.date).getTime()).slice(0, 3);
  return <div className="min-h-screen w-full overflow-hidden bg-cream" data-id="element-528" data-mp="3ss2ip6dzjzqq">
      <Header data-id="element-529" data-mp="3sslz2inzmg2n" />

      <main data-id="element-530" data-mp="3su6usbm1g523">
        <PageHero eyebrow="A space to return to yourself" heading={<>
              Welcome to{' '}
              <span className="italic text-sage-700" data-id="element-532" data-mp="5ofml6ukjlr3">
                Milangela’s Soul Garden
              </span>
            </>} body="A soulful space of support on your path back to yourself. Reconnect with yourself through sound, guided meditation and compassionate presence." image={HERO_IMAGE} imageAlt="A quiet bench overlooking a misty mountain landscape" overlay="light" size="tall" dividerFill={SURFACE.cream} buttons={[{
        label: 'Start Your Journey',
        to: '/start-your-journey'
      }, {
        label: 'Meet Milangela',
        to: '/meet-milangela',
        style: 'secondary'
      }]} data-id="element-531" data-mp="3ntiuezbx6bv7" />

        <section className="bg-cream px-6 py-20 md:py-28" data-id="element-533" data-mp="2m8t1zr9gran3">
          <TextImageSection eyebrow="The Soul Garden" heading="A space to pause, reconnect and begin again." image={INTRODUCTION_IMAGE} imageAlt="Branka Androjna holding tuning forks in her welcoming studio" paragraphs={[{
          text: 'Life has a way of leading us through seasons of change, uncertainty and challenge. In those moments, we often lose touch with ourselves beneath stress, expectations and the pace of everyday life.'
        }, {
          text: 'Milangela’s Soul Garden is a space I created so you can reconnect with yourself — through sound, guided meditation and compassionate presence. Here you’ll find individual sessions, guided practices and inspiring content designed to help you restore balance, quiet the mind and discover new ways forward.'
        }, {
          text: 'I believe that true transformation doesn’t begin by changing who we are, but by creating the right conditions for our body and mind to remember their natural wisdom and strength.'
        }, {
          text: 'You are welcome exactly as you are.',
          style: 'statement'
        }]} cta={{
          label: 'Meet Milangela',
          to: '/meet-milangela'
        }} data-id="element-534" data-mp="2qbzgfm6qc84n" />
        </section>

        <SectionDivider from={SURFACE.cream} to={SURFACE.charcoal} data-id="element-535" data-mp="24tqewbbi5ceo" />

        <QuoteBlock quote="Future Medicine will be the Medicine of Frequency." author="Albert Einstein" bgClass="bg-charcoal" textClass="text-cream" accentClass="text-rose" authorClass="text-cream-100/70" data-id="element-536" data-mp="202qm9i9f1ghz" />

        <SectionDivider from={SURFACE.charcoal} to={SURFACE.creamDeep} data-id="element-537" data-mp="28w6rpeb6b09" />

        <section className="bg-cream-200 px-6 pb-24 pt-8 md:pb-32" data-id="element-538" data-mp="3kjzo4onaxnti">
          <ServicesGrid eyebrow="Ways to work together" heading="Three paths. One intention." intro="Each path offers a different way of returning to yourself — through sound, stillness or supportive conversation." services={servicesData} data-id="element-539" data-mp="3pjcg1nt15l2y" />

          <div className="mt-12 text-center" data-id="element-540" data-mp="31mot5g8pdgjv">
            <p className="font-serif text-xl italic text-charcoal" data-id="element-541" data-mp="31nrlztpl5t2f">
              Not sure where to begin? Let’s connect.
            </p>
            <Link to="/lets-connect" className="mt-5 inline-flex items-center gap-3 bg-sage-700 px-7 py-3.5 text-xs font-medium tracking-[0.16em] text-white uppercase transition-colors hover:bg-charcoal focus:outline-none focus:ring-2 focus:ring-charcoal focus:ring-offset-2" data-id="element-542" data-mp="zutmjf1b028i">
              Let’s Connect
              <ArrowRight size={16} strokeWidth={1.5} aria-hidden="true" data-id="element-543" data-mp="1a01xzj8drh2l" />
            </Link>
          </div>
        </section>

        <section className="bg-rose-50 px-6 py-20 md:py-28" data-id="element-544" data-mp="1nrq7rfv5ptge">
          <JournalCarousel heading="Stay inspired between sessions." lead="Transformation doesn't happen only during a session." body="The Garden is a growing collection of reflections, guided meditations and conversations designed to accompany you in everyday life." ctaLabel="Visit The Garden" ctaTo="/journal" posts={latestPosts} data-id="element-545" data-mp="1iydkb927rsp8" />
        </section>

        <section className="bg-cream px-6 py-20 md:py-28" data-id="element-546" data-mp="22lbiuc4107bc">
          <TestimonialsGrid eyebrow="In their own words" heading="Thoughts from those who have walked this path." testimonials={testimonials} ctaLabel="Read all testimonials" ctaTo="/testimonials" data-id="element-547" data-mp="cbhzqywyhpxc" />
        </section>

        <section className="bg-cream px-6 pb-20 md:pb-28" data-id="element-548" data-mp="11s4ll6m8idfn">
          <div className="mx-auto max-w-3xl" data-id="element-549" data-mp="16jv0zr013rdx">
            <FaqSection eyebrow="Good to know" heading="Common questions" faqs={faqs} data-id="element-550" data-mp="16kxtu4gww3wh" />
          </div>
        </section>

        <CtaBanner heading="You don't have to walk alone." body="Whether you are seeking balance, clarity, support or a quiet place to begin again, Milangela's Soul Garden offers a space where you can be seen, heard and accepted." buttonLabel="Start Your Journey" buttonTo="/start-your-journey" dividerFrom={SURFACE.cream} data-id="element-551" data-mp="dvqrsvi90w6h" />
      </main>

      <Footer data-id="element-552" data-mp="1ybkb91z5r96o" />
    </div>;
}